import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiCheckCircle,
  FiClock,
  FiKey,
  FiLock,
  FiRefreshCcw,
  FiShield,
  FiSmartphone,
} from 'react-icons/fi';
import api from '../../../api';

const GLOBAL_PIN_MODES = [
  { value: 'required', label: 'Required' },
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'optional', label: 'Optional' },
];

const POLICY_FIELDS = [
  ['protectConnectedSystems', 'connected_services', 'Connected systems'],
  ['protectLicenseView', 'license_view', 'License view'],
  ['protectLicenseEdit', 'license_edit', 'License edit'],
  ['protectLicenseResync', 'license_resync', 'License resync'],
  ['protectRoleManagement', 'role_management', 'Role management'],
  ['protectCompanySwitch', 'company_switch', 'Company switch'],
  ['protectSystemSettings', 'system_settings', 'System settings'],
  ['protectProfilePiiView', 'profile_pii_view', 'Profile & PII view'],
];

function coerceNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function featureLabelMap(items = []) {
  return new Map(items.map((item) => [item.key, item.label]));
}

function formatDate(value) {
  if (!value) return 'Never';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function ToggleRow({ label, checked, onChange, hint }) {
  return (
    <label className="flex items-start justify-between gap-4 rounded-xl border border-[#f5b221]/12 bg-[#111111] px-4 py-3">
      <div>
        <div className="text-sm font-medium text-white">{label}</div>
        {hint ? <p className="mt-1 text-xs text-[#8f8f8f]">{hint}</p> : null}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 rounded border-[#4a4a4a] bg-[#050505] text-[#f5b221] focus:ring-[#f5b221]"
      />
    </label>
  );
}

export default function SecurityTab({ user, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingClientId, setSavingClientId] = useState('');
  const [notice, setNotice] = useState('');

  const headers = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const labels = useMemo(
    () => featureLabelMap(data?.featureCatalog || []),
    [data?.featureCatalog]
  );

  const load = useCallback(async () => {
    if (!userId) {
      setError('Missing user id');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.get(`/admin/users/${userId}/security`, { headers });
      setData(response.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load security settings.');
    } finally {
      setLoading(false);
    }
  }, [headers, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateGlobalField = (field, value) => {
    setData((current) => ({
      ...current,
      user: {
        ...(current?.user || {}),
        securityPreferences: {
          ...(current?.user?.securityPreferences || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateSystemField = (clientId, field, value) => {
    setData((current) => ({
      ...current,
      systems: (current?.systems || []).map((system) =>
        system.clientId === clientId
          ? {
              ...system,
              securityPolicy: {
                ...(system.securityPolicy || {}),
                [field]: value,
              },
            }
          : system
      ),
    }));
  };

  const saveGlobal = async () => {
    if (!data?.user?.securityPreferences) return;
    setSavingGlobal(true);
    setNotice('');
    try {
      await api.patch(
        `/admin/users/${userId}/security/preferences`,
        { securityPreferences: data.user.securityPreferences },
        { headers }
      );
      setNotice('Global security preferences saved.');
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save global security preferences.');
    } finally {
      setSavingGlobal(false);
    }
  };

  const saveSystem = async (system) => {
    setSavingClientId(system.clientId);
    setNotice('');
    try {
      await api.patch(
        `/admin/users/${userId}/security/policies/${encodeURIComponent(system.clientId)}`,
        { securityPolicy: system.securityPolicy },
        { headers }
      );
      setNotice(`Saved ${system.portalLabel || system.clientId} security policy.`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save the system security policy.');
    } finally {
      setSavingClientId('');
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#f5b221]/12 bg-[#111111] p-6 text-white">
        Loading security center...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-2xl border border-red-500/25 bg-red-500/10 p-6 text-red-100">
        {error}
      </div>
    );
  }

  const securityPreferences = data?.user?.securityPreferences || {};
  const systems = Array.isArray(data?.systems) ? data.systems : [];

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-[#f5b221]/14 bg-[linear-gradient(180deg,#171717,#0c0c0c)] p-6 shadow-lg">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#f5b221]/25 bg-[#f5b221]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#f7d88a]">
              <FiShield />
              Security Center
            </div>
            <h3 className="mt-3 text-xl font-semibold text-white">
              Secure access rules for {user?.firstName || data?.user?.firstName || 'this user'}
            </h3>
            <p className="mt-2 max-w-3xl text-sm text-[#9a9a9a]">
              Shimbirr stays the source of truth for mobile OTP. When secure access is enabled on a
              connected system, sensitive views stay locked until the app code is verified.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-xl border border-[#f5b221]/20 bg-[#111111] px-4 py-2 text-sm text-white transition hover:border-[#f5b221]/40 hover:text-[#f7d88a]"
          >
            <FiRefreshCcw />
            Refresh
          </button>
        </div>

        {notice ? (
          <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            {notice}
          </div>
        ) : null}

        {error ? (
          <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-[#f5b221]/14 bg-[#111111] p-6 shadow-lg">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#f5b221]/12 text-[#f7d88a]">
            <FiSmartphone />
          </span>
          <div>
            <h4 className="text-lg font-semibold text-white">Shimbirr app defaults</h4>
            <p className="text-sm text-[#8f8f8f]">
              These preferences shape how the mobile app PIN and synced OTP behavior should work.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          <label className="rounded-xl border border-[#f5b221]/12 bg-[#0d0d0d] p-4">
            <div className="text-sm font-medium text-white">App PIN mode</div>
            <select
              value={securityPreferences.appPinMode || 'required'}
              onChange={(event) => updateGlobalField('appPinMode', event.target.value)}
              className="mt-3 w-full rounded-lg border border-[#2f2f2f] bg-[#050505] px-3 py-2 text-sm text-white outline-none focus:border-[#f5b221]"
            >
              {GLOBAL_PIN_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </label>

          <label className="rounded-xl border border-[#f5b221]/12 bg-[#0d0d0d] p-4">
            <div className="text-sm font-medium text-white">Default unlock window</div>
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#2f2f2f] bg-[#050505] px-3">
              <FiClock className="text-[#f7d88a]" />
              <input
                type="number"
                min="30"
                max="7200"
                value={securityPreferences.defaultUnlockWindowSeconds || 900}
                onChange={(event) =>
                  updateGlobalField(
                    'defaultUnlockWindowSeconds',
                    coerceNumber(event.target.value, 900)
                  )
                }
                className="w-full bg-transparent py-2 text-sm text-white outline-none"
              />
              <span className="text-xs uppercase tracking-wide text-[#727272]">sec</span>
            </div>
          </label>

          <ToggleRow
            label="Sync login OTP with Shimbirr app"
            hint="Keeps the app as the main place for short-lived sign-in and secure-action codes."
            checked={securityPreferences.loginOtpSyncEnabled !== false}
            onChange={(checked) => updateGlobalField('loginOtpSyncEnabled', checked)}
          />
        </div>

        <div className="mt-5 flex items-center justify-between rounded-xl border border-[#f5b221]/12 bg-[#0b0b0b] px-4 py-3 text-sm text-[#9a9a9a]">
          <div className="flex items-center gap-2 text-[#f7d88a]">
            <FiKey />
            App OTP rotates every {data?.stepSeconds || 10} seconds.
          </div>
          <button
            type="button"
            onClick={() => void saveGlobal()}
            disabled={savingGlobal}
            className="rounded-xl bg-[#f5b221] px-4 py-2 text-sm font-semibold text-black transition hover:bg-[#f7d88a] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {savingGlobal ? 'Saving...' : 'Save Global Settings'}
          </button>
        </div>

        <div className="mt-4 rounded-xl border border-[#f5b221]/12 bg-[#0b0b0b] p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <FiLock className="text-[#f7d88a]" />
            Shimbirr PIN status
          </div>
          <p className="mt-2 text-sm text-[#9a9a9a]">
            {data?.user?.hasShimbirrPin
              ? 'This verified account already has an account-wide Shimbirr PIN.'
              : 'This account has not created its Shimbirr PIN yet.'}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-[#f5b221]/10 bg-[#050505] px-4 py-3">
              <div className="text-xs uppercase tracking-[0.18em] text-[#727272]">
                First set
              </div>
              <div className="mt-2 text-sm text-white">
                {formatDate(data?.user?.shimbirrPinSetAt)}
              </div>
            </div>
            <div className="rounded-xl border border-[#f5b221]/10 bg-[#050505] px-4 py-3">
              <div className="text-xs uppercase tracking-[0.18em] text-[#727272]">
                Last updated
              </div>
              <div className="mt-2 text-sm text-white">
                {formatDate(data?.user?.shimbirrPinUpdatedAt)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#f5b221]/12 text-[#f7d88a]">
            <FiLock />
          </span>
          <div>
            <h4 className="text-lg font-semibold text-white">Connected systems</h4>
            <p className="text-sm text-[#8f8f8f]">
              Enable secure access only on the systems and features this user should protect with
              mobile OTP.
            </p>
          </div>
        </div>

        {systems.length === 0 ? (
          <div className="rounded-2xl border border-[#f5b221]/12 bg-[#111111] p-6 text-sm text-[#8f8f8f]">
            No connected systems were found for this user yet.
          </div>
        ) : null}

        {systems.map((system) => {
          const policy = system.securityPolicy || {};
          return (
            <article
              key={system.clientId}
              className="rounded-2xl border border-[#f5b221]/12 bg-[linear-gradient(180deg,#151515,#0b0b0b)] p-6 shadow-lg"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h5 className="text-lg font-semibold text-white">
                      {system.portalLabel || system.partner || system.clientId}
                    </h5>
                    <span className="rounded-full border border-[#f5b221]/18 bg-[#f5b221]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#f7d88a]">
                      {system.clientId}
                    </span>
                    {policy.secureAccessEnabled ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-100">
                        <FiCheckCircle />
                        Protected
                      </span>
                    ) : (
                      <span className="rounded-full border border-[#2e2e2e] bg-[#111111] px-3 py-1 text-xs font-semibold text-[#8f8f8f]">
                        Not enforced
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-[#8f8f8f]">
                    Partner: {system.partner || 'Unknown'} · Last paired:{' '}
                    {formatDate(system.lastPairedAt)}
                  </p>
                  {Array.isArray(system.services) && system.services.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {system.services.map((service) => (
                        <span
                          key={service.id}
                          className="rounded-full border border-[#2e2e2e] bg-[#101010] px-3 py-1 text-xs text-[#bcbcbc]"
                        >
                          {service.title || service.portalLabel || service.systemKey || service.clientId}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:w-[26rem]">
                  <ToggleRow
                    label="Enable secure access"
                    hint="Switch on Shimbirr OTP checks for this system."
                    checked={policy.secureAccessEnabled === true}
                    onChange={(checked) =>
                      updateSystemField(system.clientId, 'secureAccessEnabled', checked)
                    }
                  />
                  <ToggleRow
                    label="Allow privileged role bypass"
                    hint="Highest roles can skip OTP if this stays enabled."
                    checked={policy.allowPrivilegedRoleBypass === true}
                    onChange={(checked) =>
                      updateSystemField(system.clientId, 'allowPrivilegedRoleBypass', checked)
                    }
                  />
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <label className="rounded-xl border border-[#f5b221]/12 bg-[#0d0d0d] p-4">
                  <div className="text-sm font-medium text-white">Unlock window</div>
                  <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#2f2f2f] bg-[#050505] px-3">
                    <FiClock className="text-[#f7d88a]" />
                    <input
                      type="number"
                      min="30"
                      max="7200"
                      value={policy.unlockWindowSeconds || 900}
                      onChange={(event) =>
                        updateSystemField(
                          system.clientId,
                          'unlockWindowSeconds',
                          coerceNumber(event.target.value, 900)
                        )
                      }
                      className="w-full bg-transparent py-2 text-sm text-white outline-none"
                    />
                    <span className="text-xs uppercase tracking-wide text-[#727272]">sec</span>
                  </div>
                </label>

                <label className="rounded-xl border border-[#f5b221]/12 bg-[#0d0d0d] p-4">
                  <div className="text-sm font-medium text-white">Privileged roles</div>
                  <input
                    type="text"
                    value={Array.isArray(policy.privilegedRoles) ? policy.privilegedRoles.join(', ') : ''}
                    onChange={(event) =>
                      updateSystemField(system.clientId, 'privilegedRoles', event.target.value)
                    }
                    placeholder="superadmin, admin, owner"
                    className="mt-3 w-full rounded-lg border border-[#2f2f2f] bg-[#050505] px-3 py-2 text-sm text-white outline-none focus:border-[#f5b221]"
                  />
                </label>
              </div>

              <div className="mt-5 grid gap-3 lg:grid-cols-2">
                {POLICY_FIELDS.map(([field, featureKey, fallbackLabel]) => (
                  <ToggleRow
                    key={`${system.clientId}-${field}`}
                    label={labels.get(featureKey) || fallbackLabel}
                    checked={policy[field] === true}
                    onChange={(checked) => updateSystemField(system.clientId, field, checked)}
                  />
                ))}
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => void saveSystem(system)}
                  disabled={savingClientId === system.clientId}
                  className="rounded-xl bg-[#f5b221] px-4 py-2 text-sm font-semibold text-black transition hover:bg-[#f7d88a] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savingClientId === system.clientId ? 'Saving...' : 'Save System Policy'}
                </button>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
